import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Typography, Box, Grid, Avatar } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import format from 'date-fns/format';
import addDays from 'date-fns/addDays';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import { useDispatch, useSelector } from 'react-redux';

import { Loader } from 'ui';
import { shortDateFormat } from 'shared/constants/dateFormat';
import { usePrevious } from 'shared/hooks';
import { downloadService } from 'services';
import { addNotificationAction } from 'store/notifications/notificationsActions';

import { getProductById } from '../../selectors/shopSelectors';

const useStyles = makeStyles(theme => ({
  avatar: {
    width: 200,
    height: 200,
    backgroundColor: theme.palette.primary.contrastText,
    boxShadow: '0 4px 4px 3px rgba(155, 155, 155, 0.3)',
    margin: '0 auto',
  },
  lockOpenIcon: {
    fontSize: 100,
    color: '#0cd4a4',
    fontWeight: 'bold',
  },
  termsLabel: {
    userSelect: 'none',
    lineHeight: 2,
  },
}));

const ShopProductTrialModal = ({ open, onClose, onConfirm, data }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();

  const watchedProduct = useSelector(getProductById(data.id));
  const isPurchased = usePrevious(data.purchased);
  const termsDocument =
    watchedProduct &&
    watchedProduct.documents &&
    watchedProduct.documents.find(({ name }) => name === 'Terms and conditions.pdf');

  const [isSuccess, setIsSuccess] = useState(data.purchased);
  const [isLoading, setIsLoading] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);

  const startDate = new Date();
  const endDate = addDays(startDate, data.trialPeriod);

  const handleConfirm = () => {
    setIsLoading(true);
    onConfirm();
  };

  const throwFetchError = () =>
    dispatch(
      addNotificationAction({
        variant: 'error',
        message: t('Failed to get terms and conditions. Please try again or contact support.'),
      }),
    );

  const downloadTerms = () => {
    downloadService({
      path: `/shop/file-download/${termsDocument.id}/`,
      name: termsDocument.name,
      onError: throwFetchError,
    });
  };

  useEffect(() => {
    if (!isPurchased && watchedProduct && watchedProduct.purchased) {
      setIsSuccess(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedProduct]);

  return (
    <div>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>{data.name}</DialogTitle>
        <DialogContent>
          {isSuccess ? (
            <Box width={500} height={400} py={8}>
              <Grid container alignContent="center" justifyContent="center">
                <Grid item>
                  <Avatar className={classes.avatar}>
                    <LockOpenIcon className={classes.lockOpenIcon} />
                  </Avatar>
                  <Box mt={4}>
                    <Typography variant="h6">{t('Your subscription is active')}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <>
              <Box>
                <Typography>{t('Your free trial starts today.')}</Typography>
              </Box>

              <Box mt={4}>
                <Typography>
                  {t('Subscription:')} <strong>{data.name}</strong>
                </Typography>
                <Typography>
                  {t('Start:')} <strong>{format(startDate, shortDateFormat)}</strong>
                </Typography>
                <Typography>
                  {t('End:')} <strong>{format(endDate, shortDateFormat)}</strong>
                </Typography>
                <Typography>
                  {t('Amount:')}{' '}
                  <strong>{differenceInCalendarDays(endDate, startDate)} days</strong>
                </Typography>
              </Box>

              <Box mt={4}>
                <Typography>
                  {t(
                    'The free trial provides access to the content covered by this subscription except for downloading data, which is deactivated during the period of the free trial. The trial ends automatically, without any further action needed from you.',
                  )}
                </Typography>
              </Box>
              <Box mt={4}>
                <Typography>
                  {t(
                    'If you would like to prolong the trial or get more information, please contact your personal advisor or the Service Center.',
                  )}
                </Typography>
              </Box>
            </>
          )}
          {termsDocument && (
            <Box mt={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={termsChecked}
                    onChange={event => setTermsChecked(event.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <span className={classes.termsLabel}>
                    {t('I have read and agree to the ')}
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <Link component="button" variant="body1" onClick={downloadTerms}>
                      {t('terms & conditions.')}
                    </Link>
                  </span>
                }
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {isSuccess ? (
            <Grid container justifyContent="flex-end">
              <Grid item>
                <Button color="primary" variant="contained" onClick={onClose}>
                  {t('Close')}
                </Button>
              </Grid>
            </Grid>
          ) : (
            <>
              <Button onClick={onClose} color="primary" variant="outlined">
                {t('Cancel')}
              </Button>
              {isLoading ? (
                <Box px={2}>
                  <Loader />
                </Box>
              ) : (
                <Button
                  onClick={handleConfirm}
                  color="primary"
                  variant="contained"
                  autoFocus
                  disabled={termsDocument && !termsChecked}
                >
                  {t('Confirm')}
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};

ShopProductTrialModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  data: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    trialPeriod: PropTypes.number.isRequired,
    purchased: PropTypes.bool.isRequired,
  }).isRequired,
};

export { ShopProductTrialModal };
